import React, { useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Download, Eye, FileText, X, Loader2, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

export interface Document {
    label: string;
    url?: string;
    fileName?: string;
}

interface DocumentViewerProps {
    documents: Document[];
    onDownload?: (url: string, fileName: string) => void;
    gridCols?: 2 | 3 | 4;
    /** When true, document URLs are still being resolved */
    loading?: boolean;
    /** When true, hides View and Download buttons (image is still clickable to view) */
    hideActions?: boolean;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
    documents,
    onDownload,
    gridCols = 3,
    loading = false,
    hideActions = false,
}) => {
    const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [imageLoadFailed, setImageLoadFailed] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);

    const handleView = (doc: Document) => {
        if (!doc.url) {
            toast.error('Document not available');
            return;
        }
        setSelectedDoc(doc);
        setImageLoadFailed(false);
        setImageLoading(true);
        setZoom(1);
        setRotation(0);
        setIsModalOpen(true);
    };

    const handleZoomIn = useCallback(() => setZoom(z => Math.min(z + 0.25, 3)), []);
    const handleZoomOut = useCallback(() => setZoom(z => Math.max(z - 0.25, 0.5)), []);
    const handleRotate = useCallback(() => setRotation(r => (r + 90) % 360), []);

    const handleDownload = (doc: Document) => {
        if (!doc.url) {
            toast.error('Document not available');
            return;
        }

        if (onDownload) {
            onDownload(doc.url, doc.fileName || doc.label);
        } else {
            // Default download behavior
            const link = document.createElement('a');
            link.href = doc.url;
            link.download = doc.fileName || doc.label;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success('Download started');
        }
    };

    const getUrlPath = (url?: string) => {
        try { return new URL(url || '').pathname; } catch { return url || ''; }
    };

    const isPDF = (url?: string) => {
        return getUrlPath(url).toLowerCase().endsWith('.pdf') || url?.includes('application/pdf');
    };

    const isImage = (url?: string) => {
        return getUrlPath(url).match(/\.(jpg|jpeg|png|gif|webp)$/i) != null || url?.startsWith('data:image');
    };

    const gridColsClass = {
        2: 'grid-cols-1 md:grid-cols-2',
        3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
        4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    };

    if (loading) {
        return (
            <div className={`grid ${gridColsClass[gridCols]} gap-4`}>
                {[1, 2, 3].map((i) => (
                    <Card key={i} className="overflow-hidden">
                        <CardContent className="p-4">
                            <div className="aspect-video bg-muted rounded-lg mb-3 flex items-center justify-center animate-pulse">
                                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                            </div>
                            <div className="h-4 bg-muted rounded animate-pulse mb-2" />
                            <div className="flex gap-2">
                                <div className="h-8 flex-1 bg-muted rounded animate-pulse" />
                                <div className="h-8 flex-1 bg-muted rounded animate-pulse" />
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        );
    }

    return (
        <>
            <div className={`grid ${gridColsClass[gridCols]} gap-4`}>
                {documents.map((doc, index) => (
                    <Card key={index} className="overflow-hidden">
                        <CardContent className="p-4">
                            {/* Preview Area */}
                            <div
                                className="aspect-video bg-muted rounded-lg mb-3 overflow-hidden relative group"
                            >
                                {doc.url ? (
                                    <>
                                        {isImage(doc.url) ? (
                                            <img
                                                src={doc.url}
                                                alt={doc.label}
                                                className="w-full h-full object-cover"
                                                crossOrigin="anonymous"
                                            />
                                        ) : isPDF(doc.url) ? (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <FileText className="w-16 h-16 text-muted-foreground" />
                                            </div>
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <FileText className="w-16 h-16 text-muted-foreground" />
                                            </div>
                                        )}

                                        {/* Hover Overlay */}
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                onClick={() => handleView(doc)}
                                            >
                                                <Eye className="w-4 h-4 mr-1" />
                                                View
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                onClick={() => handleDownload(doc)}
                                            >
                                                <Download className="w-4 h-4 mr-1" />
                                                Download
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <div className="text-center">
                                            <X className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                                            <p className="text-sm text-muted-foreground">Not uploaded</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Document Info */}
                            <div>
                                <p className="font-medium text-sm mb-2">{doc.label}</p>
                                {!hideActions && (
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="flex-1"
                                            onClick={() => handleView(doc)}
                                            disabled={!doc.url}
                                        >
                                            <Eye className="w-3 h-3 mr-1" />
                                            View
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="flex-1"
                                            onClick={() => handleDownload(doc)}
                                            disabled={!doc.url}
                                        >
                                            <Download className="w-3 h-3 mr-1" />
                                            Download
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Image Preview Modal */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="max-w-5xl max-h-[95vh] p-0 gap-0 overflow-hidden">
                    <DialogHeader className="px-6 pt-5 pb-3">
                        <DialogTitle className="flex items-center justify-between">
                            <span>{selectedDoc?.label}</span>
                            <div className="flex items-center gap-1 mr-8">
                                {selectedDoc?.url && !imageLoadFailed && !isPDF(selectedDoc.url) && (
                                    <>
                                        <Button size="icon" variant="ghost" onClick={handleZoomOut} title="Zoom out">
                                            <ZoomOut className="w-4 h-4" />
                                        </Button>
                                        <span className="text-xs text-muted-foreground min-w-[3rem] text-center">{Math.round(zoom * 100)}%</span>
                                        <Button size="icon" variant="ghost" onClick={handleZoomIn} title="Zoom in">
                                            <ZoomIn className="w-4 h-4" />
                                        </Button>
                                        <Button size="icon" variant="ghost" onClick={handleRotate} title="Rotate">
                                            <RotateCw className="w-4 h-4" />
                                        </Button>
                                        <div className="w-px h-6 bg-border mx-1" />
                                    </>
                                )}
                                {selectedDoc && (
                                    <Button size="sm" variant="outline" onClick={() => handleDownload(selectedDoc)}>
                                        <Download className="w-4 h-4 mr-2" />
                                        Download
                                    </Button>
                                )}
                            </div>
                        </DialogTitle>
                    </DialogHeader>
                    <div className="overflow-auto max-h-[calc(95vh-5rem)] bg-muted/30 flex items-center justify-center">
                        {selectedDoc?.url ? (
                            <>
                                {isPDF(selectedDoc.url) ? (
                                    <iframe
                                        src={selectedDoc.url}
                                        className="w-full h-[80vh] border-0"
                                        title={selectedDoc.label}
                                    />
                                ) : imageLoadFailed ? (
                                    <div className="text-center py-12">
                                        <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                                        <p className="text-muted-foreground mb-4">
                                            Unable to preview this document
                                        </p>
                                        <Button onClick={() => handleDownload(selectedDoc)}>
                                            <Download className="w-4 h-4 mr-2" />
                                            Download File
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="relative flex items-center justify-center p-4 min-h-[300px] w-full">
                                        {imageLoading && (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                                            </div>
                                        )}
                                        <img
                                            src={selectedDoc.url}
                                            alt={selectedDoc.label}
                                            className="max-w-full h-auto transition-transform duration-200"
                                            style={{
                                                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                                                transformOrigin: 'center center',
                                            }}
                                            onLoad={() => setImageLoading(false)}
                                            onError={() => {
                                                setImageLoading(false);
                                                setImageLoadFailed(true);
                                            }}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-12">
                                <X className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                                <p className="text-muted-foreground">Document not available</p>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default DocumentViewer;
